import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Icons, Label, TBody, TD, TH, THead, TR, Table } from '@sede-enterprise/shell-ds-react-framework';

import { Col, Row } from '../../components/flexbox/Flex';
import { AnchorSpan, ContainerDiv } from '../../components/styledComponent/StyledComponent';
import { ApiUrls } from '../../helpers/ApiUrls';
import { downloadFile, formatDateToDDMMMYYYY, usePageTitle } from '../../services/UtilService';
import ErrorAlert from '../../components/errorAlert/ErrorAlert';
import { getData } from '../../services/ApiService';
import ResponseErrorModal from '../../components/responseErrorModal/ResponseErrorModal';
import BackButton from '../../components/backButton/BackButton';
import Loader from '../../components/loader/Loader';
import AuthorizeContext from '../../components/applayout/AuthrizeContext';
import { APIErrorResponseInterface } from '../../helpers/InterfacesConstants';
import { useTranslation } from 'react-i18next';

interface DataObj{
    ticketDetail: {
        status: string,
        ticketNumber: string,
        ticketType: string | null,
        batchNumber: number,
        deliveryId: number,
        site: string,
        grossQuantity: number,
        totalQuantity: number,
        transactionDate: Date,
        receivedDate: Date,
        uom: string | null,
        fileName: string | null,
        createdBy: string | null,
        createdDate: string | null,
        modifiedBy: string | null,
        modifiedDate: string | null,
    },
    productDetails: [
        {
            ambientTemperature: string | null,
            code: string | null,
            localCode: string | null,
            confirmedQuantity: number | null,
            confirmedUOM: string | null,
            density: number | null,
            name: string | null,
            packTypeCode: number | null,
            storedTemperature: string | null,
            quantity: number | null,
            uom: string | null,
            type: string | null
        }
    ],
    flightDetail: {
        aircraftRegistration: string,
        finalDestination: string | null,
        flightId: string | null,
        flightOccurNumber: number,
        nextDestination: string
    },
    customerTransactionDetail: {
        cardNumber: number | null,
        companyName: string | null,
        creditType: string | null,
        customerAccountNumber: string | null,
        dutyInd: string | null,
        transactionType: string | null,
        vatInd: string | null,
        customer: string | null,
        customerCode: string | null,
        conco: string | null,
        delco: string | null,
        extInd: string | null
    },
    transactionErrorDetail: {
        totalError: number;
        errorTypeWithDetails: [
            {
                errorType: string;
                errorCodeWithDescs: [
                    {
                        reasonCode: string;
                        errorDescription: string;
                    }
                ];
            }
        ];
    },
    feesDetails:[
        {
            type: string | null,
            code: string | null,
            name: string | null,
            localCode: string | null,
            quantity: number | null
        }
    ],
    fileDetails:[
        {
            fileUploadedBy : string | null,
            fileUploadedDate:  string | null,
            blobFileName: string | null,
            remarks: string | null,
            reason : string | null,
            attachmentFileName: string | null
        }
    ]
}

interface fileDetail {
    fileUploadedBy : string | null,
    fileUploadedDate:  string | null,
    blobFileName: string | null,
    remarks: string | null,
    reason : string | null,
    attachmentFileName: string | null
}

const Details = () => {
    const transaction  = useParams();
    const [data, setData] = useState<DataObj | null>();
    const [apiErrorResponse, setApiErrorResponse] = useState<APIErrorResponseInterface | null>();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const userPermissions = useContext(AuthorizeContext);
    const miPermission = userPermissions?.permissions.find(permission => permission.permissionName === 'MI');
    const { t } = useTranslation();
    usePageTitle(t('shellAviationAIMS')+" | Details");



    const download = (fileDetail: fileDetail) =>{
        const url = ApiUrls.fileDownload + '/' + transaction.headerId + '/' +fileDetail.blobFileName + '/'+ fileDetail.reason;
        const fileName = fileDetail.attachmentFileName ? fileDetail.attachmentFileName : 'temp';
        downloadFile(url, fileName);
    }
    
    useEffect(() => {
        const url = ApiUrls.miDetails + transaction.headerId;
        setIsLoading(true);
        getData(url).then((response) => {
            if (response) {
                setData(response.data);
                setIsLoading(false);
            }
        }).catch((error)=>{
            setApiErrorResponse({
                message : error.response.data.message,
                traceId : error.response.data.traceId
            });
        });
    }, []);

    const handleCopyID = () => {
        const copyID: string  = data?.ticketDetail.ticketNumber?  data?.ticketDetail.ticketNumber : '';
        navigator.clipboard.writeText(copyID);
    };

    return (
        <>
            {isLoading && <Loader/>}
            <ContainerDiv>
                <Row>
                    <Col> 
                        <BackButton/>
                    </Col>
                </Row>
                <Row>
                    <Col size="75"> 
                        <h2 style={{margin:'0px'}}>Ticket# {data?.ticketDetail.ticketNumber}
                        <Button variant="transparent" icon={<Icons.Copy />} onClick={handleCopyID} style={{minWidth:'auto'}}></Button>
                        </h2>
                        
                    </Col>
                    <Col size="25">
                        <Row>
                            <Col size="75">
                                <Label>Ticket Status:</Label><span style={{paddingLeft:'8px'}}><strong>{data?.ticketDetail.status}</strong></span>
                            </Col>
                            
                            <Col size="25" col={0}>
                                {miPermission?.canWrite &&
                                    <Button onClick={()=>{navigate("/update/"+transaction.headerId)}}>Edit</Button>
                                }
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </ContainerDiv>

            <ContainerDiv>
                <Row>
                    { data?.transactionErrorDetail && <ErrorAlert errorDetails={data?.transactionErrorDetail}/> }
                </Row>
            </ContainerDiv>

            <ContainerDiv>
                <Card>
                    <Table size="small" align="left"  width="100%" >
                        <THead>
                            <TR>
                                <TH>Received Date</TH>
                                <TH>Transaction Date</TH>
                                <TH>Ticket Type</TH>
                                <TH>Delivery ID</TH>
                                <TH>Site</TH>
                                <TH>Gross Qty</TH>
                                <TH>Net Qty</TH>
                                <TH>UOM</TH>
                            </TR>
                        </THead>
                        <TBody>
                            <TR>
                                <TD>{data && formatDateToDDMMMYYYY(data.ticketDetail.receivedDate)}</TD>
                                <TD>{data && formatDateToDDMMMYYYY(data.ticketDetail.transactionDate)}</TD>
                                <TD>{data?.ticketDetail.ticketType}</TD>
                                <TD>{data?.ticketDetail.deliveryId}</TD>
                                <TD>{data?.ticketDetail.site}</TD>
                                <TD>{data?.ticketDetail.grossQuantity}</TD>
                                <TD>{data?.ticketDetail.totalQuantity}</TD>
                                <TD>{data?.ticketDetail.uom}</TD>
                            </TR>
                        </TBody>
                    </Table>
                </Card>
            </ContainerDiv>


            <ContainerDiv>
                <Card  header={{
                    title: 'Flight Details',
                    }}
                    >
                    <Table size="small" align="left"  width="100%" >
                        <THead>
                            <TR>
                                <TH>Flight ID</TH>
                                <TH>Aircraft Registration</TH>
                                <TH>Next Destination</TH>
                                <TH>Final Destination</TH>
                                <TH>Flight Occur Number</TH>
                            </TR>
                        </THead>
                        <TBody>
                            <TR>
                                <TD>{data?.flightDetail.flightId}</TD>
                                <TD>{data?.flightDetail.aircraftRegistration}</TD>
                                <TD>{data?.flightDetail.nextDestination}</TD>
                                <TD>{data?.flightDetail.finalDestination}</TD>
                                <TD>{data?.flightDetail.flightOccurNumber}</TD>
                            </TR>
                        </TBody>
                    </Table>
                </Card>
            </ContainerDiv>

            <ContainerDiv>
                <Card header={{
                    title: 'Customer & Transaction Details',
                    }}>
                    <Table size="small" align="left"  width="100%" >
                        <THead>
                            <TR>
                                <TH>Customer</TH>
                                <TH>Account Number</TH>
                                <TH>Conco</TH>
                                <TH>Delco</TH>
                                <TH>Transaction Type</TH>
                                <TH>Card Number</TH>
                                <TH>Credit Type</TH>
                                <TH>Vat Ind</TH>
                                <TH>Duty Ind</TH>
                            </TR>
                        </THead>
                        <TBody>
                            <TR>
                                <TD>{data?.customerTransactionDetail.customer}  {data?.customerTransactionDetail?.customerCode && <span>({data.customerTransactionDetail.customerCode})</span>}</TD>
                                <TD>{data?.customerTransactionDetail.customerAccountNumber}</TD>
                                <TD>{data?.customerTransactionDetail.conco}</TD>
                                <TD>{data?.customerTransactionDetail.delco}</TD>
                                <TD>{data?.customerTransactionDetail.transactionType}</TD>
                                <TD>{data?.customerTransactionDetail.cardNumber}</TD>
                                <TD>{data?.customerTransactionDetail.creditType}</TD>
                                <TD>{data?.customerTransactionDetail.vatInd}</TD>
                                <TD>{data?.customerTransactionDetail.dutyInd}</TD>
                            </TR>
                        </TBody>
                    </Table>
                </Card>
            </ContainerDiv>

            <ContainerDiv>
                <Card header={{
                    title: 'Product Details',
                    }}>
                    <Table size="small" align="left"  width="100%" >
                        <THead>
                            <TR>
                                <TH>Product Code</TH>
                                <TH>Name</TH>
                                <TH>Qty</TH>
                                <TH>UOM</TH>
                                <TH>Ambient Temperature</TH>
                                <TH>Stored Temperature</TH>
                                <TH>Density</TH>
                            </TR>
                        </THead>
                        <TBody>
                            {data?.productDetails.map( (product, index) => (
                                <TR key={product.code+''+index}>
                                    <TD>{product.code}</TD>
                                    <TD>{product.name}</TD>
                                    <TD>{product.quantity}</TD>
                                    <TD>{product.uom}</TD>
                                    <TD>{product.ambientTemperature}</TD>
                                    <TD>{product.storedTemperature}</TD>
                                    <TD>{product.density && Number(product.density).toFixed(2)}</TD>
                                </TR>
                            ))}
                        </TBody>
                    </Table>
                </Card>
            </ContainerDiv>

            <ContainerDiv>
                <Card header={{
                    title: 'Fees',
                    }}>
                    <Table size="small" align="left"  width="100%" >
                        <THead>
                            <TR>
                                <TH>Code</TH>
                                <TH>Name</TH>
                                <TH>Qty</TH>
                            </TR>
                        </THead>
                        <TBody>
                        {data?.feesDetails.map( (feeDetail, index) => (
                            <TR key={'fee'+''+index}>
                                <TD>{feeDetail.code}</TD>
                                <TD>{feeDetail.name}</TD>
                                <TD>{feeDetail.quantity}</TD>
                            </TR>
                        ))}
                        </TBody>
                    </Table>
                </Card>
            </ContainerDiv>

            <ContainerDiv>
                <Card header={{
                    title: 'Files & Comments'
                    }}>
                    <Table style={{tableLayout: 'fixed'}}>
                        <THead>
                            <TR>
                                <TH style={{width: '30%'}}>File Name</TH>
                                <TH style={{width: '40%'}}>Comment</TH>
                                <TH style={{width: '15%'}}>Updated By</TH>
                                <TH style={{width: '15%'}}>Updated Date</TH>
                            </TR>
                        </THead>
                        <TBody className='file-table-body'>
                        {data?.fileDetails.map( (fileDetail, index) => (
                            <TR key={'file'+''+index}>
                                <TD><AnchorSpan onClick={()=>{download(fileDetail)}}>{fileDetail?.attachmentFileName && fileDetail.attachmentFileName}</AnchorSpan></TD>
                                <TD>{fileDetail?.remarks}</TD>
                                <TD>{fileDetail?.fileUploadedBy}</TD>
                                <TD style={{textAlign: 'end'}}>{fileDetail?.fileUploadedDate && formatDateToDDMMMYYYY(fileDetail.fileUploadedDate)}</TD>
                            </TR>
                        ))}
                        </TBody>
                    </Table>
                </Card>
            </ContainerDiv>


            <ContainerDiv>
                <Card header={{
                    title: 'Created & Modified Details',
                    }}>
                    <Table style={{tableLayout: 'fixed'}} >
                        <THead>
                            <TR>
                                <TH style={{width: '25%'}}>Created By</TH>
                                <TH style={{width: '25%'}}>Created Date</TH>
                                <TH style={{width: '25%'}}>Last Modified By</TH>
                                <TH style={{width: '25%'}}>Last Modified Date</TH>
                            </TR>
                        </THead>
                        <TBody>
                            <TR key='history'>
                                <TD>{data?.ticketDetail?.createdBy}</TD>
                                <TD>{data?.ticketDetail?.createdDate && formatDateToDDMMMYYYY(data?.ticketDetail?.createdDate)}</TD>
                                <TD>{data?.ticketDetail?.modifiedBy}</TD>
                                <TD style={{textAlign: 'end'}}>{data?.ticketDetail?.modifiedDate && formatDateToDDMMMYYYY(data?.ticketDetail?.modifiedDate)}</TD>
                            </TR>
                        </TBody>
                    </Table>
                </Card>
            </ContainerDiv>


            {
                data?.ticketDetail.fileName &&
                <ContainerDiv><Label bold>File Name:</Label> { data.ticketDetail.fileName} ({ data.ticketDetail.batchNumber})</ContainerDiv>
            }

            <ResponseErrorModal errorDetails={apiErrorResponse} isOpen={apiErrorResponse? true : false} onModalClose={()=>{setApiErrorResponse(null)}}></ResponseErrorModal>
        </>
    )
}

export default Details;